import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Menu, MenuItem, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useCommentsReportReasons } from "../../../../api/queries/useCommentsReportReasons";
import { CommentReportModal } from "./CommentReportModal";
import { CommentDeleteIcon, CommentReportIcon } from "../../../../assets/icons/create-feed";
import useIsOwn from "../../../../utils/useIsOwn";
import { useDeleteCommentMutation } from "../../../../api/mutations/useDeleteCommentMutation";

interface CommentMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  authorFullName: string;
  authorId: number;
  commentId: number;
  postId: number;
}

const menuItemStyles = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
};

const menuItemTypographyStyles = {
  color: "var(--Dark---1000, #111)",
  fontFamily: "Montserrat",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: "20px",
};

const CommentMenuItem = ({
  icon,
  label,
  onClick,
  isLoading,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  isLoading?: boolean;
}) => {
  if (isLoading !== undefined) {
    return (
      <MenuItem sx={menuItemStyles} disableRipple>
        <LoadingButton
          loading={isLoading}
          onClick={onClick}
          startIcon={icon}
          sx={{
            ...menuItemTypographyStyles,
            justifyContent: "flex-start",
            padding: 0,
            minWidth: "unset",
            "&.MuiLoadingButton-root": {
              color: "var(--Dark---1000, #111)",
            },
          }}
        >
          {label}
        </LoadingButton>
      </MenuItem>
    );
  }

  return (
    <MenuItem sx={menuItemStyles} onClick={onClick}>
      {icon}
      <Typography sx={menuItemTypographyStyles}>{label}</Typography>
    </MenuItem>
  );
};

export const CommentMenu = ({ anchorEl, onClose, commentId, authorId, postId }: CommentMenuProps) => {
  const { t } = useTranslation("feed", { keyPrefix: "comment" });
  const { data: reportReasons = [] } = useCommentsReportReasons();
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  const { isOwn, isLoading: isOwnLoading } = useIsOwn({ authorId, isAuthorIdLoading: false });
  const { deleteComment, isDeleting } = useDeleteCommentMutation({ commentId, postId });

  const handleOpenReportModal = () => {
    onClose();
    setIsReportModalOpen(true);
  };

  const handleCloseReportModal = () => {
    setIsReportModalOpen(false);
  };

  const normaMenuItems = [
    {
      icon: <CommentReportIcon />,
      label: t("report"),
      onClick: handleOpenReportModal,
      isLoading: false,
    },
  ];

  const ownMenuItems = [
    {
      icon: <CommentDeleteIcon />,
      label: t("delete"),
      onClick: deleteComment,
      isLoading: isDeleting,
    },
  ];

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={Boolean(anchorEl)}
        onClose={onClose}
      >
        {(isOwn ? ownMenuItems : normaMenuItems).map(({ icon, label, onClick, isLoading }, index) => (
          <CommentMenuItem key={index} icon={icon} label={label} onClick={onClick} isLoading={isLoading} />
        ))}
      </Menu>

      <CommentReportModal
        open={isReportModalOpen}
        onClose={handleCloseReportModal}
        commentId={commentId}
        reportReasons={reportReasons || []}
      />
    </>
  );
};
